import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, FileText, Upload, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TAX_CLASSES = [
    { value: "individual", label: "Individual / Sole Proprietor" },
    { value: "sole_proprietor", label: "Single-member LLC (as SP)" },
    { value: "llc", label: "LLC (C-corp or S-corp)" },
    { value: "s_corp", label: "S-Corporation" },
    { value: "c_corp", label: "C-Corporation" },
    { value: "partnership", label: "Partnership" },
    { value: "trust_estate", label: "Trust / Estate" },
];

/**
 * Digital W-9 collection for 1099 caregivers.
 * Captures the IRS required fields + typed e-signature + optional PDF upload
 * of the signed W-9 (recommended). Backend stores only the last-4 of the TIN
 * plus the encrypted PDF in the vault.
 */
export default function W9Dialog({ open, onOpenChange, onSaved }) {
    const [status, setStatus] = useState(null);
    const [form, setForm] = useState({
        legal_name: "",
        business_name: "",
        federal_tax_class: "individual",
        tin_type: "SSN",
        tin: "",
        address_line1: "",
        address_city: "",
        address_state: "",
        address_zip: "",
        signature_name: "",
        signed_date: new Date().toISOString().slice(0, 10),
        ack_perjury: false,
    });
    const [pdfFile, setPdfFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Load existing status on open — pre-fill fields if they already submitted
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const res = await api.get("/caregiver/w9/status");
                setStatus(res.data);
                if (res.data.uploaded) {
                    setForm((f) => ({
                        ...f,
                        legal_name: res.data.legal_name || "",
                        business_name: res.data.business_name || "",
                        federal_tax_class: res.data.federal_tax_class || "individual",
                        tin_type: res.data.tin_type || "SSN",
                        address_line1: res.data.address_line1 || "",
                        address_city: res.data.address_city || "",
                        address_state: res.data.address_state || "",
                        address_zip: res.data.address_zip || "",
                    }));
                }
            } catch (e) { /* fine — brand new caregiver */ }
        })();
    }, [open]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function uploadPdfIfProvided() {
        if (!pdfFile) return null;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", pdfFile);
            fd.append("category", "w9");
            const res = await api.post("/vault/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return res.data?.id || null;
        } finally { setUploading(false); }
    }

    async function submit(e) {
        e.preventDefault();
        if (!form.ack_perjury) return toast.error("You must acknowledge the perjury certification.");
        if (!/^\d{9}$/.test((form.tin || "").replace(/\D/g, ""))) {
            return toast.error("TIN must be 9 digits (SSN or EIN)");
        }
        setSaving(true);
        try {
            const pdf_document_id = await uploadPdfIfProvided();
            await api.post("/caregiver/w9/submit", { ...form, pdf_document_id });
            toast.success("W-9 saved. You can now accept shifts.");
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save W-9");
        } finally { setSaving(false); }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="w9-dialog" className="max-w-2xl rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-outfit">
                        <FileText className="w-5 h-5 text-emerald-700" /> W-9 for 1099 caregivers
                    </DialogTitle>
                    <DialogDescription className="text-stone-600 font-dmsans">
                        All Infini HealthCare caregivers are independent 1099 contractors. Complete this form once — it stays on file for as long as you work through the platform. Your Social Security or EIN number is stored only as the last 4 digits; the full signed PDF (if you upload one) lives in your encrypted vault.
                    </DialogDescription>
                </DialogHeader>

                {status?.uploaded && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs font-dmsans text-emerald-900 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Currently on file: {status.legal_name} · {status.tin_type} ••••{status.tin_last4} · signed {status.signed_at}
                    </div>
                )}

                <form onSubmit={submit} className="space-y-3 mt-1">
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <Label>Legal name (as on your Social Security card / IRS letter) *</Label>
                            <Input value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} required data-testid="w9-legal-name" className="h-11 rounded-xl mt-1" />
                        </div>
                        <div>
                            <Label>Business name / DBA (optional)</Label>
                            <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="w9-business-name" className="h-11 rounded-xl mt-1" />
                        </div>
                    </div>

                    <div>
                        <Label>Federal tax classification *</Label>
                        <select value={form.federal_tax_class} onChange={(e) => set("federal_tax_class", e.target.value)} data-testid="w9-tax-class" className="mt-1 h-11 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm font-dmsans">
                            {TAX_CLASSES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-[1fr,2fr]">
                        <div>
                            <Label>TIN type *</Label>
                            <div className="mt-1 flex gap-2">
                                {["SSN", "EIN"].map((t) => (
                                    <button key={t} type="button" data-testid={`w9-tin-type-${t.toLowerCase()}`}
                                        data-active={form.tin_type === t}
                                        onClick={() => set("tin_type", t)}
                                        className="flex-1 h-11 rounded-xl border-2 border-stone-200 hover:border-emerald-500 data-[active=true]:bg-emerald-700 data-[active=true]:text-white data-[active=true]:border-emerald-700 font-outfit font-semibold text-sm">{t}</button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label>{form.tin_type} number * <span className="text-stone-500 font-normal">(9 digits — only last 4 kept on file)</span></Label>
                            <Input type="password" value={form.tin} onChange={(e) => set("tin", e.target.value.replace(/[^\d-]/g, "").slice(0, 11))} placeholder={form.tin_type === "SSN" ? "123-45-6789" : "12-3456789"} required data-testid="w9-tin" className="h-11 rounded-xl mt-1 font-mono" />
                        </div>
                    </div>

                    <div>
                        <Label>Address (street) *</Label>
                        <Input value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} required data-testid="w9-address" className="h-11 rounded-xl mt-1" />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-[2fr,1fr,1fr]">
                        <div>
                            <Label>City *</Label>
                            <Input value={form.address_city} onChange={(e) => set("address_city", e.target.value)} required data-testid="w9-city" className="h-11 rounded-xl mt-1" />
                        </div>
                        <div>
                            <Label>State *</Label>
                            <Input value={form.address_state} onChange={(e) => set("address_state", e.target.value.toUpperCase().slice(0, 2))} maxLength={2} required placeholder="CA" data-testid="w9-state" className="h-11 rounded-xl mt-1" />
                        </div>
                        <div>
                            <Label>ZIP *</Label>
                            <Input value={form.address_zip} onChange={(e) => set("address_zip", e.target.value.replace(/[^\d-]/g, "").slice(0, 10))} required data-testid="w9-zip" className="h-11 rounded-xl mt-1" />
                        </div>
                    </div>

                    <div>
                        <Label>Upload signed W-9 PDF <span className="text-stone-500 font-normal">(optional but recommended)</span></Label>
                        <div className="mt-1 flex items-center gap-2">
                            <input type="file" accept="application/pdf,image/*" onChange={(e) => setPdfFile(e.target.files?.[0] || null)} data-testid="w9-pdf-file" className="text-xs font-dmsans" />
                            {pdfFile && <span className="text-xs text-emerald-700 font-dmsans">{pdfFile.name}</span>}
                        </div>
                        <p className="text-[11px] text-stone-500 mt-1 font-dmsans">
                            Download the blank form from <a href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank" rel="noreferrer" className="underline">irs.gov/pub/irs-pdf/fw9.pdf</a>, sign it, then upload here. Not required today — you can add it later.
                        </p>
                    </div>

                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 space-y-2">
                        <div className="flex items-start gap-2">
                            <input type="checkbox" checked={form.ack_perjury} onChange={(e) => set("ack_perjury", e.target.checked)} data-testid="w9-ack-perjury" id="w9-ack" className="mt-1" />
                            <label htmlFor="w9-ack" className="text-xs font-dmsans text-emerald-900 cursor-pointer">
                                {status?.legal_ack_text || "Under penalties of perjury, I certify that the information above is correct."}
                            </label>
                        </div>
                        <div className="grid gap-2 sm:grid-cols-2">
                            <div>
                                <Label className="text-xs">E-signature (type your legal name)</Label>
                                <Input value={form.signature_name} onChange={(e) => set("signature_name", e.target.value)} required data-testid="w9-signature" className="h-10 rounded-xl mt-1 font-serif italic" />
                            </div>
                            <div>
                                <Label className="text-xs">Date signed</Label>
                                <Input type="date" value={form.signed_date} onChange={(e) => set("signed_date", e.target.value)} required data-testid="w9-signed-date" className="h-10 rounded-xl mt-1" />
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="mt-2 flex-row gap-2 sm:justify-between">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving} className="rounded-full flex-1 sm:flex-none">Cancel</Button>
                        <Button type="submit" disabled={saving || uploading} data-testid="w9-submit" className="rounded-full flex-1 sm:flex-none bg-emerald-700 hover:bg-emerald-800 text-white font-outfit">
                            {uploading ? <><Upload className="w-4 h-4 mr-1 animate-pulse" /> Uploading…</> : saving ? "Saving…" : <><ShieldCheck className="w-4 h-4 mr-1" /> Submit W-9</>}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
